import React, {
    PropsWithChildren,
    useEffect,
    useState,
    useCallback,
    FormEvent,
    useContext,
    SetStateAction,
    Dispatch,
} from 'react';
import {
    ComboBox,
    IComboBox,
    IComboBoxOption,
    SelectableOptionMenuItemType,
} from '@fluentui/react';
import { getDeploymentTypesList } from '../../AGSS/api/AGSSApi';
import { CancelToken } from 'axios';
import { usePromise } from '../hooks/usePromise';
import { ThemeContext } from '../../HubLayout/models/ThemeContext';
import {
    getThemeFromString,
    setSelectedDeploymentTypesInLocalStorage,
} from '../util/localStorageUtils';
import { emitUserActionMetric } from '../util/metricsUtil';
import {
    ActionResult,
    FilterTypes,
    UserAction,
} from '../../Common/Enums/Metrics/MetricEnums';

import {
    componentComboBoxStyles,
    componentDivStyles,
} from '../util/CommonStyles';
import { SortSelectedOptions } from '../util/sortUtils';
import { comboBoxUtils } from '../util/comboBoxUtils';

interface IDeploymentTypeDropDownProps extends PropsWithChildren<any> {
    selectedDeploymentTypes: string[];
    setSelectedDeploymentTypes?: Dispatch<SetStateAction<string[]>>;
    setPageNumber?: Dispatch<SetStateAction<number>>;
    isLoaded: boolean;
    isMobile?: boolean;
    placeholder?: string;
}

export const DeploymentTypeDropDown: React.FC<IDeploymentTypeDropDownProps> = (
    props: IDeploymentTypeDropDownProps
) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const [options, setOptions] = useState<IComboBoxOption[]>([]);
    const disabled = props.isLoaded ? false : true;

    const getData = useCallback(async (cancelToken?: CancelToken) => {
        return await getDeploymentTypesList(cancelToken);
    }, []);

    const [result, , dropdownLoaded] = usePromise(getData, true);

    const selectableOptions = options.filter(
        (option) =>
            (option.itemType === SelectableOptionMenuItemType.Normal ||
                option.itemType === undefined) &&
            !option.disabled
    );

    const sortedOptions = SortSelectedOptions(
        selectableOptions,
        props.selectedDeploymentTypes,
        'Deployment Types'
    );

    const onChange = (
        event: FormEvent<IComboBox>,
        option?: IComboBoxOption,
        index?: number,
        value?: string
    ) => {
        // Prevent the selection from being cleared when the combobox loses focus.
        // With`allowFreeform`, Fluent UI's ComboBox commits any pending typed
        // value on blur and routes that commit through onChange. We only care
        // about user-initiated selection changes, so ignore blur-driven calls.
        if (event?.type === 'blur') {
            return;
        }

        comboBoxUtils(
            option,
            props.selectedDeploymentTypes,
            selectableOptions,
            props.setSelectedDeploymentTypes!,
            props.setPageNumber,
            setSelectedDeploymentTypesInLocalStorage,
            () =>
                emitUserActionMetric(
                    UserAction.DROPDOWN_FILTER,
                    FilterTypes.DEPLOYMENT_TYPE,
                    ActionResult.SUCCESS,
                    process.env.REACT_APP_BUILD_VERSION
                )
        );
    };

    const comboBoxStyles = componentComboBoxStyles(
        props.selectedDeploymentTypes.length,
        theme,
        props.isMobile
    );

    const divStyles = componentDivStyles(props.isMobile);

    useEffect(() => {
        if (dropdownLoaded && result) {
            let comboboxOptions: IComboBoxOption[] = [
                {
                    key: 'selectAll',
                    text: 'Select All',
                    itemType: SelectableOptionMenuItemType.SelectAll,
                },
                {
                    key: 'Header1',
                    text: 'Deployment Types',
                    itemType: SelectableOptionMenuItemType.Header,
                },
            ];
            const deploymentTypes = result.split(',');

            deploymentTypes.forEach((deploymentType) => {
                var option = {
                    key: deploymentType,
                    text: deploymentType,
                } as IComboBoxOption;

                comboboxOptions.push(option);
            });

            setOptions(comboboxOptions);
        }
    }, [result, dropdownLoaded]);

    return (
        <div style={divStyles}>
            {result && (
                <ComboBox
                    multiSelect
                    placeholder={props.placeholder || 'Deployment Types'}
                    ariaLabel={props.placeholder || 'Deployment Types'}
                    options={sortedOptions}
                    selectedKey={props.selectedDeploymentTypes}
                    onChange={onChange}
                    styles={comboBoxStyles}
                    disabled={disabled}
                    allowFreeform
                />
            )}
        </div>
    );
};
